import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Pagination } from '../Pagination/Pagination';
import type { Size } from '../types';

export interface DataTableColumn<T> {
  /** Stable key for the column (also the default sort key). */
  key: string;
  header: ReactNode;
  /** Cell renderer; defaults to the raw value at `key` if the row has one. */
  render?: (row: T) => ReactNode;
  /** Enable click-to-sort on this column's header. */
  sortable?: boolean;
  /** Value used for sorting (defaults to `render`-free raw value at `key`). */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Extra class on the <th>/<td>. */
  className?: string;
  /** Right-align numeric columns, etc. */
  align?: 'start' | 'center' | 'end';
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Show the built-in search box (client-side, matches across all columns' text). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /**
   * Controlled search query. When provided, the table filters by this and does NOT render its own
   * search box (the caller owns the input, e.g. in a card header). Leave undefined for the built-in.
   */
  searchQuery?: string;
  /** Paginate client-side at this page size (omit / 0 to disable). */
  pageSize?: number;
  onRowClick?: (row: T) => void;
  /** Add a leading checkbox column for row selection. */
  selectable?: boolean;
  onSelectionChange?: (keys: string[]) => void;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  size?: Extract<Size, 'sm'>;
  /** Initial sort, e.g. { key: 'date', dir: 'desc' }. */
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  emptyMessage?: ReactNode;
  className?: string;
  /** Extra class(es) applied to a row's <tr> (e.g. a table-variant to highlight matching rows). */
  rowClassName?: (row: T) => string | undefined;
  /** Content rendered on the left of the header bar, next to the search box (e.g. a filter). */
  toolbar?: ReactNode;
}

const rawValue = <T,>(row: T, key: string): unknown =>
  (row as Record<string, unknown>)[key];

const asText = (v: unknown): string => (v == null ? '' : String(v));

/**
 * Limitless-styled, generic, client-side data table: sortable headers, an optional search box,
 * pagination, row selection and row-click. Dependency-free (the jQuery DataTables plugin is not
 * used). Sorting/search/pagination all run in the browser over `rows`.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  searchable = false,
  searchPlaceholder = 'Search…',
  searchQuery,
  pageSize = 0,
  onRowClick,
  selectable = false,
  onSelectionChange,
  striped = false,
  hover = false,
  bordered = false,
  size,
  initialSort,
  emptyMessage = 'No data to display.',
  className = '',
  rowClassName,
  toolbar,
}: DataTableProps<T>) {
  const [internalQuery, setInternalQuery] = useState('');
  // Controlled query (from the caller) takes precedence over the built-in search box.
  const query = searchQuery ?? internalQuery;
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort ?? null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset to the first page when a controlled query changes (the built-in box resets inline).
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const colSortValue = (col: DataTableColumn<T>, row: T) =>
    col.sortValue ? col.sortValue(row) : (rawValue(row, col.key) as string | number | null | undefined);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((c) => {
        const v = c.sortValue ? c.sortValue(row) : rawValue(row, c.key);
        return asText(v).toLowerCase().includes(q);
      }),
    );
  }, [rows, columns, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = colSortValue(col, a);
      const bv = colSortValue(col, b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return asText(av).localeCompare(asText(bv), undefined, { numeric: true }) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, columns]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, totalPages);
  const paged = pageSize > 0 ? sorted.slice((safePage - 1) * pageSize, safePage * pageSize) : sorted;

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    setSort((s) =>
      s?.key === col.key ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: 'asc' },
    );
  };

  const emitSelection = (next: Set<string>) => {
    setSelected(next);
    onSelectionChange?.([...next]);
  };
  const toggleRow = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    emitSelection(next);
  };
  const pageKeys = paged.map(getRowKey);
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const toggleAllOnPage = () => {
    const next = new Set(selected);
    if (allOnPageSelected) pageKeys.forEach((k) => next.delete(k));
    else pageKeys.forEach((k) => next.add(k));
    emitSelection(next);
  };

  const tableClasses = [
    'table',
    striped ? 'table-striped' : '',
    hover || onRowClick ? 'table-hover' : '',
    bordered ? 'table-bordered' : '',
    size === 'sm' ? 'table-sm' : '',
    'align-middle mb-0',
  ]
    .filter(Boolean)
    .join(' ');

  const sortIcon = (col: DataTableColumn<T>) => {
    if (!col.sortable) return null;
    if (sort?.key !== col.key) return <i className="ph-arrows-down-up ms-1 opacity-50" />;
    return <i className={`ph-caret-${sort.dir === 'asc' ? 'up' : 'down'} ms-1`} />;
  };

  return (
    <div className={className}>
      {((searchable && searchQuery === undefined) || toolbar) && (
        <div className="d-flex align-items-center gap-2 p-3 pb-2">
          {toolbar}
          {searchable && searchQuery === undefined && (
            <div className="form-control-feedback form-control-feedback-start ms-auto" style={{ maxWidth: 280 }}>
              <input
                type="search"
                className="form-control"
                placeholder={searchPlaceholder}
                value={internalQuery}
                onChange={(e) => {
                  setInternalQuery(e.target.value);
                  setPage(1);
                }}
              />
              <div className="form-control-feedback-icon">
                <i className="ph-magnifying-glass opacity-50" />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="table-responsive">
        <table className={tableClasses}>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${col.align ? `text-${col.align}` : ''} ${col.sortable ? 'cursor-pointer user-select-none' : ''} ${col.className ?? ''}`.trim()}
                  onClick={() => toggleSort(col)}
                  style={col.sortable ? { cursor: 'pointer' } : undefined}
                >
                  {col.header}
                  {sortIcon(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paged.map((row) => {
                const key = getRowKey(row);
                return (
                  <tr
                    key={key}
                    className={[selected.has(key) ? 'table-active' : '', rowClassName?.(row) ?? '']
                      .filter(Boolean)
                      .join(' ') || undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {selectable && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selected.has(key)}
                          onChange={() => toggleRow(key)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className={`${col.align ? `text-${col.align}` : ''} ${col.className ?? ''}`.trim()}>
                        {col.render ? col.render(row) : asText(rawValue(row, col.key))}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-2">
          <span className="text-muted small">
            Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of{' '}
            {sorted.length}
          </span>
          <Pagination totalPages={totalPages} currentPage={safePage} onPageChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
}
